import { ApiError, NetworkError, TimeoutError } from './errors';
import type { RetryPolicy } from './policy';

// Какие HTTP-статусы безопасно повторять (только транзиентные). 429 — с уважением к Retry-After.
const RETRYABLE_STATUS = new Set([429, 502, 503, 504]);

/** Повторяем только транзиентные ошибки. Решение о идемпотентности принимается выше (в runner). */
export function isRetryableError(error: unknown): boolean {
  if (error instanceof NetworkError || error instanceof TimeoutError) {
    return true;
  }
  if (error instanceof ApiError) {
    return RETRYABLE_STATUS.has(error.status);
  }
  return false;
}

/** Экспоненциальный backoff с джиттером: задержка в диапазоне 50–100% от capped-значения. */
export function backoffDelayMs(attempt: number, policy: RetryPolicy): number {
  const exp = policy.baseDelayMs * 2 ** Math.max(0, attempt - 1);
  const capped = Math.min(exp, policy.maxDelayMs);
  const jitter = capped * 0.5 * Math.random();
  return Math.round(capped * 0.5 + jitter);
}

/** Если сервер прислал Retry-After (через ApiError.retryAfterMs) — уважаем его. */
export function retryAfterMs(error: unknown): number | undefined {
  if (error instanceof ApiError && typeof error.retryAfterMs === 'number') {
    return error.retryAfterMs;
  }
  return undefined;
}

/** Парсит заголовок Retry-After (секунды или HTTP-дата) в миллисекунды. */
export function parseRetryAfter(value: string | undefined, nowMs: number): number | undefined {
  if (!value) {
    return undefined;
  }
  const seconds = Number(value);
  if (Number.isFinite(seconds)) {
    return Math.max(0, seconds * 1000);
  }
  const dateMs = Date.parse(value);
  if (Number.isFinite(dateMs)) {
    return Math.max(0, dateMs - nowMs);
  }
  return undefined;
}
